import { createContext, lazy, Suspense, useContext, useEffect, useRef, useState, type MouseEvent, type ReactNode } from "react"
import { cn } from "@/lib/utils"
import { DIFF_HIGHLIGHT_CLASS, isNodeChanged, type ChangedRange, type NodePosition } from "../lib/diffRangeHighlight"
import { patchParagraph, type DocumentEdit } from "../lib/paragraph-edit"

const ParagraphEditor = lazy(() => import("./ParagraphEditor"))

/**
 * AN ORDER IS EDITABLE WHERE IT STANDS.
 *
 * No edit mode, no toolbar, no bar across the page: the officer clicks a
 * paragraph and types, as in a word processor, and clicks away. What he typed
 * is saved on the way out. Nothing about the page changes when he starts — the
 * paragraph keeps its place, its width and its type — because an officer fixing
 * one word should not have to notice that he has entered anything.
 *
 * Reading is untouched. A drag-selection is still a selection (the Ask-AI
 * toolbar), a link is still a link, and headings and tables are not editable at
 * all: they are the order's structure and its computed figures.
 *
 * The host decides what a save is (`DocumentEditing.onCommit`): the appellate
 * flow runs the wording guard and may refuse with reasons; the adjudication
 * flow writes the draft. A refusal is shown on the paragraph, with the
 * officer's text still in it.
 */
export interface DocumentEditing {
  /** Resolves to `null` when saved, else to why it was not, in the officer's words. */
  onCommit: (edit: DocumentEdit) => Promise<string[] | null>
  /** True while the order cannot take an edit (a run is writing it). */
  disabled?: boolean
}

export interface SectionEditContextValue {
  /** The markdown of the section being rendered, and where it starts in the document. */
  text: string
  startOffset: number
  editing: DocumentEditing
  changedRanges: ChangedRange[] | undefined
}

export const SectionEditContext = createContext<SectionEditContextValue | null>(null)

type Phase = "idle" | "editing" | "saving" | "settling" | "refused"

type MdNode = { position?: NodePosition & { start: { offset?: number }; end: { offset?: number } } } | undefined

/**
 * The `p` of an editable document. A module-level component on purpose: the
 * renderer rebuilds its component MAP whenever the highlights change, and a
 * component defined inside that map would be a new type each time — every
 * paragraph would remount and an open editor would vanish mid-word.
 */
export function EditableParagraph({ node, children }: { node?: MdNode; children?: ReactNode }) {
  const section = useContext(SectionEditContext)
  const className = cn(isNodeChanged(node?.position, section?.changedRanges) && DIFF_HIGHLIGHT_CLASS)
  const from = node?.position?.start.offset
  const to = node?.position?.end.offset
  if (!section || from === undefined || to === undefined) return <p className={className}>{children}</p>
  return (
    <EditableBlock
      className={className}
      source={section.text.slice(from, to)}
      start={section.startOffset + from}
      end={section.startOffset + to}
      editing={section.editing}
    >
      {children}
    </EditableBlock>
  )
}

function EditableBlock({
  className,
  source,
  start,
  end,
  editing,
  children,
}: {
  className: string
  source: string
  start: number
  end: number
  editing: DocumentEditing
  children: ReactNode
}) {
  const [phase, setPhase] = useState<Phase>("idle")
  const [at, setAt] = useState<{ x: number; y: number } | null>(null)
  const [reasons, setReasons] = useState<string[]>([])
  // Where the paragraph is NOW: an earlier save may have moved it while this
  // one was open.
  const where = useRef({ start, end, source })
  where.current = { start, end, source }
  const editedFrom = useRef(source)

  // The saved text has arrived in the document: the editor has done its job.
  useEffect(() => {
    if (phase === "settling" && source !== editedFrom.current) setPhase("idle")
  }, [phase, source])
  useEffect(() => {
    if (phase !== "settling") return
    const id = setTimeout(() => setPhase("idle"), 2500)
    return () => clearTimeout(id)
  }, [phase])

  const open = (event: MouseEvent<HTMLParagraphElement>) => {
    if (editing.disabled) return
    // A drag is a SELECTION (Ask AI), a link is a link: neither is "edit this".
    if (!window.getSelection()?.isCollapsed) return
    if ((event.target as HTMLElement).closest("a")) return
    editedFrom.current = source
    setAt({ x: event.clientX, y: event.clientY })
    setReasons([])
    setPhase("editing")
  }

  const leave = async ({ baseline, edited }: { baseline: string; edited: string }) => {
    const now = where.current
    const markdown = patchParagraph(now.source, baseline, edited)
    if (markdown === now.source) return setPhase("idle")
    if (!markdown.trim()) {
      setReasons(["A paragraph cannot be left empty here. Undo, or type what it should say."])
      return setPhase("refused")
    }
    setPhase("saving")
    const refused = await editing.onCommit({ start: now.start, end: now.end, source: now.source, markdown })
    if (refused) {
      setReasons(refused)
      return setPhase("refused")
    }
    setPhase("settling")
  }

  if (phase === "idle") {
    return (
      <p className={className} data-editable={editing.disabled ? undefined : ""} onClick={open}>
        {children}
      </p>
    )
  }
  return (
    <div className={cn("doc-edit", phase === "refused" && "doc-edit--refused")} data-doc-editing="" data-phase={phase}>
      <Suspense fallback={<p className={className}>{children}</p>}>
        <ParagraphEditor
          source={editedFrom.current}
          at={at}
          locked={phase !== "editing"}
          onLeave={(result) => void leave(result)}
          onCancel={() => setPhase("idle")}
        />
      </Suspense>
      {phase === "saving" && (
        <span className="doc-edit__status" role="status">
          Saving…
        </span>
      )}
      {phase === "refused" && (
        <div className="doc-edit__refused" role="alert" data-testid="doc-edit-refused">
          <div className="doc-edit__refused-title">Not saved — your text is still here.</div>
          {reasons.map((reason) => (
            <div key={reason}>{reason}</div>
          ))}
          <div className="doc-edit__refused-actions">
            <button type="button" className="linklike" onClick={() => setPhase("editing")}>
              Keep editing
            </button>
            <button type="button" className="linklike" onClick={() => setPhase("idle")}>
              Undo my change
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
